import { pb } from './pocketbase';
import { signCerfa, signConvention } from './signature.service';
import { incrementFolderCount } from './consumption.service';

export const getFolderById = async (folderId) => {
    try {
        const records = await pb.collection('folders').getOne(folderId, {
            expand: 'student_id,company_id,formation_id,center_id,tutor_id,contract_id,opco_id'
        });
        return records;
    } catch (error) {
        if (error.status === 404) {
            return [];
        }
        throw error;
    }
};

export const createFolder = async (folderData) => {
    try {
        if (pb.authStore.isValid && pb.authStore.model) {
            const partnerId = pb.authStore.record.partner_id;
            const folderWithPartnerId = {
                ...folderData,
                partner_id: partnerId
            };
            const createdFolder = await pb.collection('folders').create(folderWithPartnerId);
            
            // Incrémenter le compteur de consommation
            if (partnerId) {
                await incrementFolderCount(partnerId);
            }
            
            return createdFolder;
        } else {
            console.error('Aucun utilisateur authentifié. Impossible de créer le dossier sans partner_id.');
            return null;
        }
    } catch (error) {
        console.error('Erreur lors de la création du dossier:', error);
        throw error;
    }
};

export const createDraftFolder = async (draftData) => {
    try {
        if (pb.authStore.isValid && pb.authStore.model) {
            const partnerId = pb.authStore.record.partner_id;
            
            // 1. Créer d'abord le contrat
            const contractData = {
                student_id: draftData.student?.id,
                company_id: draftData.company?.id,
                formation_id: draftData.formation,
                promotion_id: draftData.promotion,
                center_id: draftData.center?.id,
                partner_id: partnerId,
                contract_type: 11,
                status: "brouillon"
            };
            
            const createdContract = await pb.collection('contracts').create(contractData);
            
            // 2. Créer le folder avec le contract_id
            const draftFolderData = {
                partner_id: partnerId,
                student_id: draftData.student?.id,
                formation_id: draftData.formation,
                promotion_id: draftData.promotion,
                company_id: draftData.company?.id,
                center_id: draftData.center?.id,
                contract_id: createdContract.id,
                progression: draftData.progressionJson,
                status: "brouillon"
            };
            
            const createdDraftFolder = await pb.collection('folders').create(draftFolderData);
            
            // 3. Incrémenter le compteur de consommation
            if (partnerId) {
                await incrementFolderCount(partnerId);
            }
            
            return {
                folder: createdDraftFolder,
                contract: createdContract
            };
        } else {
            console.error('Aucun utilisateur authentifié. Impossible de créer le dossier brouillon sans partner_id.');
            return null;
        }
    } catch (error) {
        console.error('Erreur lors de la création du dossier et contrat:', error);
        throw error;
    }
};

export const updateDraftFolder = async (folderId, updateData) => {
    try {
        const updatedFolder = await pb.collection('folders').update(folderId, updateData);
        return updatedFolder;
    } catch (error) {
        console.error(`Erreur lors de la mise à jour du dossier brouillon ${folderId}:`, error);
        throw error;
    }
};

export const updateFolderFormationData = async (folderId, formationData) => {
    try {
        // Préparer les données pour PocketBase selon le schéma de la collection folders
        const updatePayload = {
            training_start_date: formationData.trainingStartDate || null,
            training_end_date: formationData.trainingEndDate || null,
            course_duration: formationData.trainingDuration ? parseInt(formationData.trainingDuration, 10) : null,
            number_of_hoursinstudy: formationData.remoteTrainingDuration ? parseInt(formationData.remoteTrainingDuration, 10) : null,
            cost_diplome: formationData.annualCost ? parseFloat(formationData.annualCost) : null,
            out_of_pocket_expense_of_0: formationData.freeOfCharge === 'Oui' ? 'true' : 'false',
            type_of_contract: Array.isArray(formationData.contractType) ? formationData.contractType : [formationData.contractType || 'Apprentissage']
        };

        // Supprimer les valeurs null pour éviter les erreurs PocketBase
        Object.keys(updatePayload).forEach(key => {
            if (updatePayload[key] === null || updatePayload[key] === '') {
                delete updatePayload[key];
            }
        });

        console.log('Mise à jour du dossier avec les données de formation:', updatePayload);
        
        const updatedFolder = await pb.collection('folders').update(folderId, updatePayload);
        return updatedFolder;
    } catch (error) {
        console.error(`Erreur lors de la mise à jour des données de formation du dossier ${folderId}:`, error);
        throw error;
    }
};

export const deleteFolder = async (folderId) => {
    try {
        await pb.collection('folders').delete(folderId);
        return true;
    } catch (error) {
        console.error(`Erreur lors de la suppression du dossier ${folderId}:`, error);
        throw error;
    }
};

export const getFoldersByStudentId = async (studentId) => {
    try {
        const records = await pb.collection('folders').getFullList({
            filter: `student_id = "${studentId}"`,
            expand: 'company_id'
        });
        return records;
    } catch (error) {
        if (error.status === 404) {
            return [];
        }
        console.error(`Erreur lors de la récupération des dossiers de l'étudiant ${studentId}:`, error);
        throw error;
    }
};

export const getFoldersByCompanyId = async (companyId) => {
    try {
        const records = await pb.collection('folders').getFullList({
            filter: `company_id = "${companyId}"`,
            expand: 'student_id,formation_id,center_id,promotion_id'
        });
        return records;
    } catch (error) {
        if (error.status === 404) {
            return [];
        }
        console.error(`Erreur lors de la récupération des dossiers de l'entreprise ${companyId}:`, error);
        throw error;
    }
};

export const updateFolder = async (folderId, updateData) => {
    try {
        const updatedFolder = await pb.collection('folders').update(folderId, updateData);
        return updatedFolder;
    } catch (error) {
        console.error('Erreur lors de la mise à jour du dossier:', error);
        throw error;
    }
};

export const validateCerfaFolder = async (folderId) => {
    try {
        // Récupérer le dossier actuel avec toutes les relations
        const folder = await pb.collection('folders').getOne(folderId, {
            expand: 'contract_id,contract_id.student_id,contract_id.company_id,contract_id.center_id'
        });
        
        // Récupérer la progression actuelle
        let progression = folder.progression || {};
        
        // Mettre à jour la progression pour marquer le CERFA comme validé
        progression.ecole = {
            ...progression.ecole,
            valide_cerfa: true
        };
        
        // IMPORTANT: Sauvegarder d'abord la validation avant d'envoyer les signatures
        const updatedFolder = await pb.collection('folders').update(folderId, {
            progression: progression
        });
        
        // Ensuite, essayer d'envoyer les documents à la signature
        // Si cela échoue, la validation est quand même sauvegardée
        try {
            await signCerfa(folderId);
            await signConvention(folderId);
        } catch (signError) {
            console.error('Erreur lors de l\'envoi des signatures (validation sauvegardée):', signError);
        }
        
        return updatedFolder;
    } catch (error) {
        console.error('Erreur lors de la validation du CERFA:', error);
        throw error;
    }
};

export const getStudentByFolderId = async (folderId) => {
    try {
        const folder = await pb.collection('folders').getOne(folderId, {
            expand: 'student_id'
        });
        
        if (folder.expand && folder.expand.student_id) {
            return folder.expand.student_id;
        } else {
            throw new Error('Aucun étudiant associé à ce dossier');
        }
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'étudiant par dossier:', error);
        throw error;
    }
};

export const getFolderList = async (page = 1, perPage = 20, searchFilter = '', progressionFilter = 'all') => {
    try {
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Utilisateur non authentifié');
        }

        console.log('getFolderList - progressionFilter reçu:', progressionFilter);

        let baseFilter = `partner_id = "${pb.authStore.record.partner_id}"`;
        
        // Filtre de recherche texte (multi-termes)
        if (searchFilter) {
            const searchTerms = searchFilter.trim().split(/\s+/).filter(term => term.length > 0);
            const termFilters = searchTerms.map(term => 
                `(student_id.first_name ~ "${term}" || student_id.last_name ~ "${term}" || company_id.name ~ "${term}")`
            );
            baseFilter += ` && ${termFilters.join(' && ')}`;
        }
        
        // Filtre basé sur la progression
        switch (progressionFilter) {
            case 'pending_school':
                // Dossiers en attente de l'école (cerfa non généré ou non validé)
                baseFilter += ` && (progression.ecole.cerfa_genere = false || progression.ecole.valide = false)`;
                console.log('Filtre pending_school appliqué');
                break;
            case 'pending_company':
                // Dossiers en attente de l'entreprise (infos non remplies ou non validées)
                baseFilter += ` && (progression.entreprise.infos_remplies = false || progression.entreprise.valide = false)`;
                console.log('Filtre pending_company appliqué');
                break;
            case 'pending_student':
                // Dossiers en attente de l'étudiant (infos non remplies ou non validées)
                baseFilter += ` && (progression.etudiant.infos_remplies = false || progression.etudiant.valide = false)`;
                console.log('Filtre pending_student appliqué');
                break;
            case 'pending_opco':
                // Dossiers en attente de l'OPCO (OPCO non assigné)
                baseFilter += ` && progression.entreprise.opco_assigne = false`;
                console.log('Filtre pending_opco appliqué');
                break;
            case 'blocked':
                // Dossiers bloqués (aucune partie n'a validé)
                baseFilter += ` && progression.ecole.valide = false && progression.entreprise.valide = false && progression.etudiant.valide = false`;
                console.log('Filtre blocked appliqué');
                break;
            case 'all':
            default:
                // Pas de filtre supplémentaire
                console.log('Aucun filtre de progression appliqué (all)');
                break;
        }
        
        console.log('Filtre final:', baseFilter);

        const records = await pb.collection('folders').getList(page, perPage, {
            filter: baseFilter,
            expand: 'student_id,company_id,formation_id,promotion_id,contract_id,opco_id',
            sort: '-created'
        });

        return {
            success: true,
            data: records.items,
            totalItems: records.totalItems,
            totalPages: records.totalPages,
            page: records.page,
            perPage: records.perPage
        };
    } catch (error) {
        console.error('Erreur lors de la récupération des dossiers:', error);
        return {
            success: false,
            error: error.message,
            data: [],
            totalItems: 0,
            totalPages: 0,
            page: 1,
            perPage: perPage
        };
    }
};

export const getFolderCountsByStatus = async () => {
    try {
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Utilisateur non authentifié');
        }

        const partnerId = pb.authStore.record.partner_id;
        const baseFilter = `partner_id = "${partnerId}"`;
        
        // Compter tous les dossiers
        const totalFolders = await pb.collection('folders').getList(1, 1, {
            filter: baseFilter,
            fields: 'id'
        });

        // Compter les dossiers en attente de l'école
        const pendingSchool = await pb.collection('folders').getList(1, 1, {
            filter: `${baseFilter} && (progression.ecole.cerfa_genere = false || progression.ecole.valide = false)`,
            fields: 'id'
        });

        // Compter les dossiers en attente de l'entreprise
        const pendingCompany = await pb.collection('folders').getList(1, 1, {
            filter: `${baseFilter} && (progression.entreprise.infos_remplies = false || progression.entreprise.valide = false)`,
            fields: 'id'
        });

        // Compter les dossiers en attente de l'étudiant
        const pendingStudent = await pb.collection('folders').getList(1, 1, {
            filter: `${baseFilter} && (progression.etudiant.infos_remplies = false || progression.etudiant.valide = false)`,
            fields: 'id'
        });

        // Compter les dossiers en attente de l'OPCO
        const pendingOpco = await pb.collection('folders').getList(1, 1, {
            filter: `${baseFilter} && progression.entreprise.opco_assigne = false`,
            fields: 'id'
        });

        // Compter les dossiers bloqués
        const blocked = await pb.collection('folders').getList(1, 1, {
            filter: `${baseFilter} && progression.ecole.valide = false && progression.entreprise.valide = false && progression.etudiant.valide = false`,
            fields: 'id'
        });

        const statusCounts = {
            total: totalFolders.totalItems,
            pending_school: pendingSchool.totalItems,
            pending_company: pendingCompany.totalItems,
            pending_student: pendingStudent.totalItems,
            pending_opco: pendingOpco.totalItems,
            blocked: blocked.totalItems
        };

        return {
            success: true,
            data: statusCounts
        };
    } catch (error) {
        console.error('Erreur lors du comptage des dossiers par statut:', error);
        return {
            success: false,
            error: error.message,
            data: {
                total: 0,
                pending_school: 0,
                pending_company: 0,
                pending_student: 0,
                pending_opco: 0,
                blocked: 0
            }
        };
    }
};
